import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../auth/AuthContext'
import { authApi } from '../../api/auth'
import { PageLoader } from '../PageLoader'
import { TotpSection } from './TotpSection'

/** Роли, для которых 2FA обязательна всегда (зеркало серверного ROLES_REQUIRING_TOTP). */
const ELEVATED_ROLES = new Set(['VICE_PRINCIPAL', 'PRINCIPAL', 'IT_ADMIN', 'ADMIN', 'SUPER_ADMIN'])

/**
 * Wrapper для authenticated-страниц (6.1). Если политика требует 2FA (роль или
 * флаг школы «2FA для всех»), а она не настроена — показывает только экран
 * настройки. Это не единственная защита: без 2FA сервер выдаёт лишь
 * ограниченный токен, с которым остальное API отвечает 403 mfa_setup_required.
 * После подтверждения кода сервер сразу выдаёт полноценную сессию, статус
 * инвалидируется — и рендерятся children.
 */
export function TotpEnforcementGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const { teacher, logout } = useAuth()
  const status = useQuery({
    queryKey: ['auth', 'totp', 'status'],
    queryFn: () => authApi.totpStatus(),
    enabled: !!teacher,
    staleTime: 60_000,
  })

  if (!teacher) return <>{children}</>
  // Пока статус неизвестен, приложение не рендерим: с токеном настройки его
  // запросы всё равно получили бы 403.
  if (status.isLoading) return <PageLoader />
  // Сеть/сервер недоступны — не блокируем UI: доступ всё равно решает сервер.
  if (status.isError || !status.data) return <>{children}</>
  if (!status.data.required || status.data.enabled) return <>{children}</>

  const elevated = ELEVATED_ROLES.has(teacher.role)

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-soft to-white p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-lg">
        <div className="flex items-center gap-3 mb-4">
          <img src="/edpad-logo.png" alt="EdPad" className="w-10 h-10 rounded-lg" />
          <div className="flex-1">
            <h1 className="text-xl font-semibold">{t('totpEnforcementGate.title')}</h1>
            <p className="text-sm text-gray-500">{t('totpEnforcementGate.subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={() => { void logout() }}
            className="text-sm text-gray-500 hover:text-primary"
          >
            {t('common.logout')}
          </button>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900 mb-4">
          {elevated ? (
            <>
              {t('totpEnforcementGate.noticePrefix')}
              <strong>{roleLabel(teacher.role, t)}</strong>
              {t('totpEnforcementGate.noticeSuffix')}
            </>
          ) : (
            t('totpEnforcementGate.noticeSchool')
          )}
        </div>

        <TotpSection />
      </div>
    </div>
  )
}

function roleLabel(role: string, t: TFunction): string {
  return {
    VICE_PRINCIPAL: t('totpEnforcementGate.roleVicePrincipal'),
    PRINCIPAL: t('totpEnforcementGate.rolePrincipal'),
    ADMIN: t('totpEnforcementGate.roleAdmin'),
    IT_ADMIN: t('totpEnforcementGate.roleAdmin'),
    SUPER_ADMIN: t('totpEnforcementGate.roleAdmin'),
  }[role] ?? role
}
